/**
 * Normalized entity models for the portfolio domain (TanStack DB rows).
 *
 * Conventions that hold across every model in this file:
 * - `portfolioId` is always the backend `strategyInstanceId` — a "portfolio"
 *   and a "strategy instance" are the same thing.
 * - Numeric timestamps (`createdAt`, `updatedAt`, `timestamp`, `*RunAt`, …)
 *   are epoch milliseconds; ISO-string exceptions are called out per field.
 * - `*Percent`/`*Pct` fields are on a 0-100 percentage-point scale, not 0-1.
 * - Asset ids are the legacy `"<address>:<chainRef>"` form (EVM addresses
 *   lowercased), not CAIP.
 */
import type { VmId } from "~/lib/auth/vm-types";

const EVM_ADDRESS_PATTERN = /^0x[a-fA-F0-9]{40}$/;
const SUI_ADDRESS_PATTERN = /^0x[a-fA-F0-9]{64}$/;
const SOLANA_ADDRESS_PATTERN = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;

/**
 * Parsed form of a legacy asset id `"<address>:<chainRef>"`, split on the
 * last `:`. `vm` is inferred from the address shape (EVM hex vs Solana
 * base58); `id` is the re-normalized `"<address>:<chainRef>"` string.
 */
export interface ParsedPortfolioAssetId {
  address: string;
  chainRef: string;
  id: string;
  vm: VmId;
}

/**
 * Recursive JSON value. Opaque backend payloads (e.g. `strategyData`) are
 * validated into this shape rather than typed structurally.
 */
export type PortfolioJsonValue =
  | boolean
  | null
  | number
  | string
  | readonly PortfolioJsonValue[]
  | { readonly [key: string]: PortfolioJsonValue };

/**
 * A portfolio (backend "strategy instance"). The root entity the dashboard
 * hangs off: positions, vaults, schedules, performance, and history all key
 * back to this row's `id` (= strategyInstanceId) via `portfolioId`.
 */
export interface PortfolioInstanceModel {
  archived: boolean;
  /** Effective edit (version) of the blueprint this portfolio runs. */
  blueprintEditId?: string | null;
  /** Strategy blueprint (reusable template) this portfolio was created from. */
  blueprintId?: string | null;
  /**
   * Strategy lineage. `undefined` means the payload predates lineage support
   * (or came from a stale cache) and callers may fall back to the public
   * response; explicit `null` is authoritative — there is no origin.
   * Resolve with `resolveStrategyOrigin`, never by reading these directly.
   */
  canonicalStrategyBlueprintId?: string | null;
  /** Copilot control-plane rollup: convergence state + review queue counts. */
  controlPlane?: PortfolioControlPlaneSummaryModel;
  cashSummary?: PortfolioCashSummaryModel | null;
  createdAt: number;
  description?: string;
  /** See {@link canonicalStrategyBlueprintId} for the undefined/null contract. */
  forkedFromBlueprintId?: string | null;
  id: string;
  /** True when this portfolio is a mirrored copy of another portfolio. */
  isMirrored?: boolean | null;
  isPublic: boolean;
  name: string;
  /**
   * Deterministic smart-account (HD) index of the owning investing account,
   * as a decimal string (bigint on the backend); null when absent.
   */
  ownerAccountIndex?: string | null;
  ownerAddress: string;
  ownerVm: VmId;
  /** Raw strategy definition JSON; parse before use. */
  strategyData?: PortfolioJsonValue | null;
  updatedAt?: number;
  /** Viewer-relative permission; defaults to `viewerIsOwner` when unset. */
  viewerCanManage?: boolean;
  viewerIsOwner?: boolean;
}

/**
 * Convergence lifecycle of a portfolio moving toward its target allocation:
 * awaiting_review → queued → moving → aligned, with blocked (policy) and
 * failed as off-ramps.
 */
export type PortfolioConvergenceState =
  | "awaiting_review"
  | "queued"
  | "moving"
  | "blocked"
  | "aligned"
  | "failed";

/** The only control-plane approval/convergence target kind today. */
export type PortfolioControlPlaneTargetType = "portfolio_update";

/**
 * One actionable approval item in the control-plane review queue (a proposed
 * portfolio change awaiting the owner's decision).
 */
export interface PortfolioReviewQueueItemModel {
  approvalId: string;
  status:
    | "pending"
    | "approved"
    | "rejected"
    | "dismissed"
    | "blocked"
    | "failed"
    | "superseded";
  summary: string;
  targetId: string;
  targetType: PortfolioControlPlaneTargetType;
  title: string;
  updatedAt: number;
}

/**
 * Review-queue counts by state. `activeApprovalCount` and
 * `awaitingReviewCount` both count pending approvals; `failedCount` counts
 * failed approvals plus failed runs; `movingCount`/`queuedCount` count runs.
 */
export interface PortfolioReviewQueueSummaryModel {
  activeApprovalCount: number;
  awaitingReviewCount: number;
  blockedCount: number;
  failedCount: number;
  latestItem?: PortfolioReviewQueueItemModel;
  movingCount: number;
  queuedCount: number;
}

/**
 * Current convergence status for a portfolio, plus ids of the control-plane
 * entities (approval/proposal/run) driving it.
 */
export interface PortfolioConvergenceSummaryModel {
  /** Status of the underlying execution run, when one exists. */
  executionStatus?:
    | "accepted"
    | "queued"
    | "running"
    | "awaiting_user"
    | "completed"
    | "failed"
    | "cancelled"
    | null;
  approvalId?: string;
  proposalId?: string;
  runId?: string;
  state: PortfolioConvergenceState;
  summary: string;
  targetId?: string;
  targetType?: PortfolioControlPlaneTargetType | null;
  updatedAt: number;
}

/**
 * Control-plane rollup nested on {@link PortfolioInstanceModel} (not its own
 * collection): live convergence status + review-queue summary.
 */
export interface PortfolioControlPlaneSummaryModel {
  convergence: PortfolioConvergenceSummaryModel;
  reviewQueue: PortfolioReviewQueueSummaryModel;
}

/**
 * A concrete onchain token (one address on one chain), carrying effective
 * display metadata. Keyed by legacy asset id: `id` = `"<address>:<chainRef>"`.
 */
export interface PortfolioAssetModel {
  address: string;
  chainRef: string;
  /** Token decimals (base-unit exponent). */
  decimals: number;
  /**
   * Frontend-only sentinel set when display metadata never resolved (no icon
   * and name === symbol); lets the UI render a degraded row deliberately.
   */
  displayMetadataStatus?: "metadata_unavailable";
  iconUrl?: string;
  id: string;
  name: string;
  symbol: string;
  vm: VmId;
}

/**
 * Membership link: this asset belongs to the portfolio's tradable universe.
 * Built client-side from the detail payload's asset list.
 * `id` = `"<portfolioId>:<assetId>"`.
 */
export interface PortfolioAssetUniverseModel {
  assetId: string;
  id: string;
  portfolioId: string;
  /** Client ingestion time (Date.now() at map time), not a server field. */
  updatedAt: number;
}

/**
 * Latest USD spot price for an asset. Keyed by asset: `id` = `assetId`.
 */
export interface PortfolioAssetPriceModel {
  assetId: string;
  id: string;
  /**
   * Declared but not populated by any current mapper — 24h change is carried
   * on {@link PortfolioDashboardAssetSummaryModel} instead.
   */
  priceChange24hPercent?: number;
  priceUsd: number;
  /** Price oracle/provenance label. */
  source?: string;
  updatedAt: number;
}

/**
 * A holding: quantity of one asset in one portfolio.
 * `id` = `"<portfolioId>:<assetId>"`.
 */
export interface PortfolioPositionModel {
  assetId: string;
  id: string;
  lastLedgerTimestamp?: number;
  portfolioId: string;
  /** Human-unit decimal string (already scaled by token decimals). */
  quantity: string;
  /** Base-token integer units as a string; only set on live positions. */
  rawQuantity?: string;
  /**
   * Provenance marker; live rows use "performance-live" so stale live
   * positions can be swept without touching ledger-derived rows.
   */
  source?: string;
  updatedAt: number;
  /** quantity × priceUsd at normalization time. */
  valueUsd?: number;
}

export interface PortfolioDashboardAssetSummaryShareCanonicalModel {
  assetCanonicalId: string;
  name: string | null;
  ticker: string;
}

export interface PortfolioDashboardAssetSummaryShareRollupErrorModel {
  code: string;
  kind: string;
  message: string;
}

export type PortfolioDashboardAssetSummaryShareRollupHoldingStatus =
  | "settled"
  | "in_transit";

export interface PortfolioDashboardAssetSummaryShareRollupIssuanceModel {
  assetDbId: number;
  assetId: string;
  asOf: number;
  chainId: string;
  holdingStatus?: PortfolioDashboardAssetSummaryShareRollupHoldingStatus;
  issuerSlug: string | null;
  provisional: boolean;
  rawAmount: string;
  shares: string;
  sharesPerToken: string;
  tokenSymbol: string;
}

export interface PortfolioDashboardAssetSummaryShareRollupRefusedIssuanceModel {
  assetDbId: number;
  assetId: string | null;
  chainId: string | null;
  holdingStatus?: PortfolioDashboardAssetSummaryShareRollupHoldingStatus;
  issuerSlug: string | null;
  rawAmount: string;
  reason: PortfolioDashboardAssetSummaryShareRollupErrorModel | null;
  tokenSymbol: string | null;
}

export type PortfolioDashboardAssetSummaryShareRollupModel =
  | {
      canonical: PortfolioDashboardAssetSummaryShareCanonicalModel;
      issuances: PortfolioDashboardAssetSummaryShareRollupIssuanceModel[];
      kind: "priced";
      provisional: boolean;
      provisionalValueUsd: string;
      sharePriceUsd: string;
      shares: string;
      valueUsd: string;
      verifiedValueUsd: string;
    }
  | {
      canonical: PortfolioDashboardAssetSummaryShareCanonicalModel | null;
      errors: PortfolioDashboardAssetSummaryShareRollupErrorModel[];
      issuances: PortfolioDashboardAssetSummaryShareRollupRefusedIssuanceModel[];
      kind: "refused";
      provisional: false;
    };

export interface PortfolioHoldingBrandingModel {
  kind: "tradfi";
  ticker: string;
}

/**
 * Aggregated per-asset dashboard row: folds one or more concrete positions
 * into a single display unit, grouped under a canonical asset (same
 * underlying across chains/wrappers) or a single concrete token.
 * `id` = `"<portfolioId>:canonical:<assetCanonicalId>"` or
 * `"<portfolioId>:concrete:<assetId>"`.
 */
export interface PortfolioDashboardAssetSummaryModel {
  /** Canonical grouping id; present when `displayKind` is "canonical". */
  assetCanonicalId?: string;
  /** Authoritative canonical artwork identity; absent for crypto and raw tokens. */
  branding?: PortfolioHoldingBrandingModel;
  /** Distinct chains among the folded positions. */
  chainCount: number;
  /** "canonical" = grouped under a canonical asset; "concrete" = one raw token. */
  displayKind: "canonical" | "concrete";
  displayName: string;
  iconUrl?: string;
  id: string;
  marketCapUsd?: number;
  portfolioId: string;
  positionCount: number;
  priceChange1hPercent?: number;
  priceChange24hPercent?: number;
  priceUsd?: number;
  inTransitQuantity: string | null;
  inTransitValueUsd: number;
  /**
   * Summed human-unit decimal string; null when any component quantity is
   * unknown (then `quantityDisplay` falls back to "N positions").
   */
  quantity: string | null;
  quantityDisplay: string;
  shareRollup?: PortfolioDashboardAssetSummaryShareRollupModel;
  /** Concrete asset ids folded into this summary row. */
  sourceAssetIds: string[];
  /** Position ids folded into this summary row. */
  sourcePositionIds: string[];
  symbol: string;
  updatedAt: number;
  valueUsd: number;
}

export interface PortfolioCashSummaryConstituentModel {
  assetCanonicalId?: string;
  assetSummaryId: string;
  displayName: string;
  iconUrl?: string;
  inTransitValueUsd: number;
  quantityDisplay: string;
  symbol: string;
  valueUsd: number;
}

export interface PortfolioCashInFlightAllocationModel {
  accountRef: string;
  asOf: string;
  assetId: string;
  chainRef: string;
  decimals: number;
  holdId: string | null;
  lifecycleId: string;
  movementRef: string | null;
  operationId: string | null;
  rawAmount: string;
  /** Open discriminator: unknown roles stay ignored until their UI lands. */
  role: string;
  state: string;
  valueUsd: number | null;
}

export interface PortfolioCashSummaryModel {
  /**
   * Targetless holds-reduced settled cash. Destination-scoped buying power
   * comes from the spec-driven buy preview instead.
   */
  holdsReducedSettledCashUsd?: number;
  holdsReducedUsd?: number;
  cashInFlightAllocations?: PortfolioCashInFlightAllocationModel[];
  constituentAssetSummaryIds: string[];
  constituents: PortfolioCashSummaryConstituentModel[];
  id: string;
  inTransitUsd: number;
  portfolioId: string;
  totalUsd: number;
  updatedAt: number;
}

/**
 * The per-portfolio onchain custody account (smart-account vault) that holds
 * the portfolio's funds — one row per (portfolio, chain). Keyed by CAIP-10
 * account: `id` = `accountId` = the vault's CAIP account id.
 */
export interface PortfolioVaultModel {
  /** CAIP-10 account id (chain + onchain address); also the row `id`. */
  accountId: string;
  /** Normalized chain-native vault address. */
  address: string;
  /** CAIP-2 namespace, e.g. "eip155" | "solana". */
  chainNamespace: string;
  /**
   * Legacy numeric chain id, e.g. "1" for Ethereum, "1399811149" for Solana
   * (server maps the Solana genesis-hash CAIP-2 reference to the numeric id;
   * treat via `isSolanaChainRef` when matching, never string-compare raw).
   */
  chainRef: string;
  createdAt: number;
  id: string;
  portfolioId: string;
  updatedAt: number;
  /** Coarse VM bucket derived from `chainNamespace`. */
  vm: VmId;
}

/**
 * The portfolio's rebalance/automation schedule (singular, from the detail
 * payload). `id` = `"<portfolioId>:<scheduleId ?? "schedule">"`.
 */
export interface PortfolioScheduleModel {
  id: string;
  /** Schedule exists and is not paused. */
  isActive: boolean;
  nextRunAt?: number;
  portfolioId: string;
  /** Untransformed backend schedule payload. */
  raw?: unknown;
  /** Backend schedule id (string here; numeric on scheduled functions). */
  scheduleId?: string;
  /** Client ingestion time (Date.now() at map time). */
  updatedAt: number;
}

/**
 * An automation workflow attached to a portfolio (rebalance, buy, sell,
 * deposit, …). `id` = `"<portfolioId>:<workflowId>"`.
 */
export interface PortfolioWorkflowModel {
  description?: string | null;
  id: string;
  /** Workflow kind, e.g. "rebalance" | "buy" | "sell" | "deposit". */
  kind: string;
  lastRunAt?: number;
  nextRunAt?: number;
  portfolioId: string;
  /** Untransformed backend workflow payload. */
  raw?: unknown;
  /** Workflow lifecycle status, e.g. "draft" … "archived". */
  status: string;
  title: string;
  /** Trigger kind from the workflow's trigger spec. */
  triggerKind?: string;
  updatedAt: number;
  workflowId: string;
}

/**
 * A recurring scheduled function (e.g. recurring_account_deposit), possibly
 * cross-portfolio: `sourcePortfolioId` owns the schedule, `portfolioId` is
 * the portfolio it acts on (a deposit scheduled from A into B has source=A,
 * portfolioId=B). `id` = `"<portfolioId>:<scheduleId>"`.
 */
export interface PortfolioScheduledFunctionModel {
  config?: unknown;
  /** Hash of the function config, used for change detection/dedup. */
  configFingerprint: string;
  endAt?: number;
  /** Function type key, e.g. "recurring_account_deposit". */
  functionKey: string;
  id: string;
  /** Run interval in milliseconds. */
  intervalMs: number;
  /** Soft-delete flag from the backend. */
  isDeleted: boolean;
  lastRunAt?: number;
  /** Target portfolio the function acts on (resolved from config/targeting). */
  portfolioId: string;
  /** Untransformed backend payload. */
  raw?: unknown;
  /** Numeric backend schedule id (string on PortfolioScheduleModel). */
  scheduleId: number;
  /** Portfolio that owns/originated the scheduled function. */
  sourcePortfolioId: string;
  startAt?: number;
  /** Client ingestion time (Date.now() at map time). */
  updatedAt: number;
}

/**
 * One TVL point on the portfolio value chart.
 * `id` = backend point id, else `"<portfolioId>:<timestamp>:<index>"`.
 */
export interface PortfolioPerformancePointModel {
  /** Number of cash flows occurring at this point. */
  flowsCount: number;
  id: string;
  /** True when the point aggregates multiple sub-points/buckets. */
  isAggregate: boolean;
  portfolioId: string;
  timestamp: number;
  valueUsd: number;
}

/**
 * One point on the flow-aware performance (TWR) series. The current live
 * value point uses `id` = `"<portfolioId>:live"`; historical points use
 * `"<portfolioId>:<timestamp>:<eventType ?? "snapshot">:<index>"`.
 */
export interface PortfolioPerformanceSeriesPointModel {
  /** Cash-flow amount at this point in USD (0 when no flow). */
  amountUsd: number;
  /** Cumulative time-weighted return, percent (0-100 scale). */
  cumulativeTwrPct?: number;
  /** Flow/event label for the point, or null for plain snapshots. */
  eventType?: string | null;
  /** Period growth multiplier (1.05 = +5%). */
  growthFactor?: number;
  id: string;
  /** True for the single realtime "now" point appended to the series. */
  isLive: boolean;
  portfolioId: string;
  timestamp: number;
  valueUsd: number;
}

/**
 * Scalar performance summary for one portfolio. `id` = `portfolioId`.
 */
export interface PortfolioPerformanceSummaryModel {
  currentValueUsd?: number;
  endValueUsd?: number;
  /** Estimated fees the platform saved the user, USD. */
  feesSavedUsd?: number;
  /**
   * Deposit/flow-adjusted return percent (0-100 scale) — excludes value
   * changes caused by deposits/withdrawals themselves.
   */
  flowAdjustedReturnPercent?: number;
  id: string;
  /** Portfolio is too new to have meaningful performance history. */
  isNewPortfolio?: boolean;
  /** Error message when live valuation failed; null/undefined when healthy. */
  liveDataError?: string | null;
  netDepositsUsd?: number;
  /** Simple percentage change over the window, percent (0-100 scale). */
  percentageChange?: number;
  portfolioId: string;
  startValueUsd?: number;
  /** Client ingestion time (Date.now() at map time). */
  updatedAt: number;
}

/** Return over one trailing window; `returnPct` is percent (0-100 scale). */
export interface PortfolioPerformanceWindowReturn {
  absoluteChangeUsd: number;
  returnPct: number;
}

/**
 * Per-trailing-window (day/week/month/year/all-time) returns for one
 * portfolio. `id` = `portfolioId`.
 */
export interface PortfolioPerformanceWindowStatsModel {
  allTime?: PortfolioPerformanceWindowReturn;
  day?: PortfolioPerformanceWindowReturn;
  id: string;
  month?: PortfolioPerformanceWindowReturn;
  portfolioId: string;
  /** Client ingestion time (Date.now() at map time). */
  updatedAt: number;
  week?: PortfolioPerformanceWindowReturn;
  year?: PortfolioPerformanceWindowReturn;
}

/**
 * A portfolio activity/history event (deposit, trade, …).
 * `id` = backend id, else `"<portfolioId>:<timestamp>:<eventType>:<index>"`.
 */
export interface PortfolioHistoryEventModel {
  amountUsd?: number;
  assetId?: string;
  /** Backend event type string; "activity" fallback when absent. */
  eventType: string;
  id: string;
  /** Raw backend history item (snake_case shape), kept for detail views. */
  payload?: unknown;
  portfolioId: string;
  timestamp: number;
}

/**
 * Per-asset profit/loss for a portfolio, in USD.
 * `id` = backend id, else `"<portfolioId>:<assetId>:<index>"`.
 */
export interface PortfolioAssetPnlModel {
  assetId: string;
  id: string;
  pnlUsd?: number;
  portfolioId: string;
  /** Client ingestion time (Date.now() at map time). */
  updatedAt: number;
}

/**
 * An annotation marker on an allocation-history checkpoint; currently only
 * rebalance markers exist.
 */
export interface PortfolioAllocationHistoryAnnotationModel {
  /** Allocation event id this marker was derived from. */
  eventId: string;
  id: string;
  /** Human status text for the rebalance, or null. */
  statusLabel: string | null;
  /** ISO-8601 string (unlike most timestamps in this file, not epoch ms). */
  timestamp: string;
  title: string;
  type: "rebalance";
  /** Workflow run that produced the rebalance, or null. */
  workflowRunId: string | null;
}

/**
 * One asset row inside an allocation-history checkpoint. Unresolved
 * valuations (no USD price at that time) surface as null values with
 * `valuationResolved` false.
 */
export interface PortfolioAllocationHistoryAssetModel {
  /** Share of portfolio, percent (0-100, normalized to sum 100); null when unresolved. */
  allocationPct: number | null;
  assetId: string;
  marketValueUsd: number | null;
  priceUsd: number | null;
  /** Human-unit decimal string. */
  quantity: string;
  /** Whether a USD price/value was resolved for this asset at the checkpoint. */
  valuationResolved: boolean;
}

/**
 * A point-in-time snapshot of the portfolio's allocation (asset weights +
 * rebalance annotations), used by the allocation-history chart.
 * `id` = `"<portfolioId>:<checkpointId>"`.
 */
export interface PortfolioAllocationHistoryCheckpointModel {
  annotations: PortfolioAllocationHistoryAnnotationModel[];
  assets: PortfolioAllocationHistoryAssetModel[];
  checkpointId: string;
  /** Source event ids folded into this checkpoint. */
  eventIds: string[];
  id: string;
  /** True when at least one asset's valuation was unresolved. */
  isPartial: boolean;
  portfolioId: string;
  /** ISO-8601 string from the backend; use `timestampMs` for math. */
  timestamp: string;
  /** Epoch ms parsed from `timestamp` (0 when unparseable). */
  timestampMs: number;
  /** Summed USD value over resolved assets only (excludes unresolved). */
  totalResolvedValueUsd: number;
  /** Asset ids whose valuation could not be resolved. */
  unresolvedAssetIds: string[];
}

/** Trim and lowercase hex-address VMs; other VMs pass through as-is. */
export function normalizePortfolioAddress(address: string): string {
  const normalized = address.trim();
  if (
    EVM_ADDRESS_PATTERN.test(normalized) ||
    SUI_ADDRESS_PATTERN.test(normalized)
  ) {
    return normalized.toLowerCase();
  }

  return normalized;
}

/** Infer the VM from unambiguous address shape. */
export function inferVmFromAddress(address: string): VmId {
  if (SUI_ADDRESS_PATTERN.test(address)) {
    return "sui";
  }

  if (EVM_ADDRESS_PATTERN.test(address)) {
    return "evm";
  }

  if (SOLANA_ADDRESS_PATTERN.test(address)) {
    return "solana";
  }

  return "other:unknown";
}

/**
 * Parse a legacy asset id `"<address>:<chainRef>"` (split on the LAST `:`,
 * so addresses containing `:` in other VMs still parse). Returns null on
 * malformed input.
 */
export function parsePortfolioAssetId(
  rawAssetId: string,
): ParsedPortfolioAssetId | null {
  const normalized = rawAssetId.trim();
  if (normalized.length === 0) {
    return null;
  }

  const separatorIndex = normalized.lastIndexOf(":");
  if (separatorIndex <= 0 || separatorIndex === normalized.length - 1) {
    return null;
  }

  const address = normalizePortfolioAddress(
    normalized.slice(0, separatorIndex),
  );
  const chainRef = normalized.slice(separatorIndex + 1).trim();

  if (address.length === 0 || chainRef.length === 0) {
    return null;
  }

  return {
    address,
    chainRef,
    id: `${address}:${chainRef}`,
    vm: inferVmFromAddress(address),
  };
}

/** Parse a number or numeric string; undefined for anything non-finite. */
export function coerceFiniteNumber(
  value: string | number | null | undefined,
): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return undefined;
}

/** Coerce to a non-negative integer, falling back when unparseable. */
export function coerceFiniteInteger(value: unknown, fallback: number): number {
  const candidate = coerceFiniteNumber(
    typeof value === "string" || typeof value === "number"
      ? value
      : value == null
        ? value
        : undefined,
  );
  if (candidate === undefined) {
    return fallback;
  }

  return Math.max(0, Math.floor(candidate));
}

/** Strict boolean read: anything non-boolean yields the fallback. */
export function coerceBoolean(value: unknown, fallback = false): boolean {
  return typeof value === "boolean" ? value : fallback;
}
